import React from 'react';
import {
  ShieldCheck, UserCheck, CreditCard, Handshake,
  CheckCircle2, ArrowRight, Quote, Building2, User,
  TrendingUp, Recycle,
} from 'lucide-react';
import { NamedLink } from '../../../components';

/**
 * HowItWorksSection: pixel-for-pixel port of the Replit how-it-works.tsx page.
 * Used as a custom PageBuilder section for the How It Works page.
 * Registered in SECTION_TYPE_MAP as 'constructionHowItWorks'.
 */
const HowItWorksSection = () => (
  <div className="flex flex-col w-full">

    {/* ── Hero ── */}
    <section className="py-20 bg-muted/30 border-b border-border">
      <div className="container mx-auto px-4 text-center max-w-4xl">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 uppercase tracking-tight">
          How It Works
        </h1>
        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed">
          A secure, transparent process from listing to payout. We protect both buyers and sellers every step of the way.
        </p>
      </div>
    </section>

    {/* ── Who uses our platform? ── */}
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 uppercase">Who uses our platform?</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Whether you're a large contractor or a weekend DIYer, our marketplace connects those who have excess materials with those who need them.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              Icon: Building2,
              accentCls: 'bg-primary',
              iconCls: 'text-primary',
              title: 'Businesses & Contractors',
              desc: 'Liquidate excess inventory from completed jobs. Turn wasted materials into recovered capital and free up valuable warehouse space.',
              bullets: ['Recover sunk costs', 'Improve sustainability metrics'],
              btnLabel: 'Register as Business',
            },
            {
              Icon: User,
              accentCls: 'bg-secondary',
              iconCls: 'text-secondary',
              title: 'Individuals & DIYers',
              desc: 'Find high-quality materials at a fraction of retail cost for your home projects, or sell leftover supplies from your own renovations.',
              bullets: ['Save money on projects', 'Keep materials out of landfills'],
              btnLabel: 'Register as Individual',
            },
          ].map(({ Icon, accentCls, iconCls, title, desc, bullets, btnLabel }) => (
            <div key={title} className="group relative overflow-hidden rounded-xl border border-border bg-card p-8 shadow-sm hover:shadow-md transition-all">
              <div className={`absolute top-0 left-0 w-2 h-full ${accentCls}`} />
              <Icon className={`h-12 w-12 ${iconCls} mb-6`} />
              <h3 className="text-2xl font-bold uppercase mb-3">{title}</h3>
              <p className="text-muted-foreground mb-6">{desc}</p>
              <ul className="space-y-2 mb-8">
                {bullets.map(b => (
                  <li key={b} className="flex items-center gap-2 text-sm font-medium">
                    <TrendingUp className="h-4 w-4 text-green-500 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <NamedLink
                name="SignupPage"
                className="block w-full rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold uppercase text-center hover:bg-accent"
              >
                {btnLabel}
              </NamedLink>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Step-by-step guide ── */}
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="space-y-24">

          {/* Step 1 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 bg-card border border-border p-8 rounded-xl shadow-sm">
              <div className="space-y-6">
                {[
                  { Icon: UserCheck, bg: 'bg-primary/10', color: 'text-primary', title: 'Create an Account', desc: 'Sign up using your email, Google, or Apple account. Setup takes less than a minute.' },
                  { Icon: ShieldCheck, bg: 'bg-secondary/10', color: 'text-secondary', title: 'Stripe Verification', desc: 'To ensure a safe marketplace, all sellers must complete a quick ID verification through Stripe before listing items.' },
                  { Icon: CreditCard, bg: 'bg-accent/10', color: 'text-accent', title: 'Link Payment Methods', desc: 'All users need to link a credit card for easy checkout and a bank account to receive direct deposits.' },
                ].map(({ Icon, bg, color, title, desc }) => (
                  <div key={title} className="flex items-start gap-4">
                    <div className={`mt-1 ${bg} p-2 rounded-full ${color} flex-shrink-0`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg uppercase mb-1">{title}</h4>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-2xl mb-6">1</div>
              <h2 className="text-3xl font-bold mb-4 uppercase">Register & Verify</h2>
              <p className="text-lg text-muted-foreground">
                We maintain a high-trust environment. Whether you're buying or selling, our seamless onboarding ensures everyone on the platform is verified and real.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-full bg-secondary/10 text-secondary flex items-center justify-center font-bold text-2xl mb-6">2</div>
              <h2 className="text-3xl font-bold mb-4 uppercase">List or Find Materials</h2>
              <p className="text-lg text-muted-foreground">
                Sellers list excess inventory with photos, dimensions, and material details. Buyers search by category, condition, and distance from their job site.
              </p>
            </div>
            <div className="bg-card border border-border p-8 rounded-xl shadow-sm">
              <div className="space-y-6">
                {[
                  { label: 'List an Item', sub: 'Photos, details, and price', icon: ArrowRight },
                  { label: 'Buyer Purchases', sub: 'Pays securely upfront via platform', icon: ArrowRight },
                  { label: 'Funds Secured', sub: 'Money goes into Escrow', icon: CheckCircle2, greenIcon: true },
                ].map(({ label, sub, icon: Icon, greenIcon }, i) => (
                  <div key={label} className={`flex items-center justify-between ${i < 2 ? 'border-b border-border pb-4' : ''}`}>
                    <div>
                      <div className="font-bold">{label}</div>
                      <div className="text-sm text-muted-foreground">{sub}</div>
                    </div>
                    <Icon className={`w-5 h-5 flex-shrink-0 ${greenIcon ? 'text-green-500' : 'text-muted-foreground'}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 bg-card border border-border p-8 rounded-xl shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full" />
              <div className="relative text-center flex flex-col items-center">
                <Handshake className="w-16 h-16 text-primary mb-6" />
                <h3 className="font-bold text-xl uppercase mb-3">The Pickup Code</h3>
                <p className="text-muted-foreground mb-6">
                  After paying, the buyer receives a secure 6-digit code. They meet the seller, inspect the materials, and if satisfied, they hand the code to the seller.
                </p>
                <div className="bg-muted px-6 py-4 rounded-lg border border-border inline-flex items-center gap-4">
                  <span className="text-sm font-bold uppercase text-muted-foreground">Code:</span>
                  <span className="font-mono text-2xl tracking-[0.2em] font-bold">892-410</span>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 rounded-full bg-accent/10 text-accent flex items-center justify-center font-bold text-2xl mb-6">3</div>
              <h2 className="text-3xl font-bold mb-4 uppercase">Inspect & Handshake</h2>
              <p className="text-lg text-muted-foreground">
                Our built-in escrow protects both parties. The buyer knows their money is safe until they see the item. The seller knows the funds are guaranteed before they hand over the materials.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-full bg-green-500/10 text-green-600 flex items-center justify-center font-bold text-2xl mb-6">4</div>
              <h2 className="text-3xl font-bold mb-4 uppercase">Receive Funds Instantly</h2>
              <p className="text-lg text-muted-foreground">
                No waiting weeks for checks to clear. As soon as the transaction is marked complete, the money is on its way to your bank.
              </p>
            </div>
            <div className="bg-secondary text-secondary-foreground p-8 rounded-xl shadow-sm">
              <div className="space-y-6">
                {[
                  ['Enter Code', "The seller enters the buyer's 6-digit Pickup Code into their dashboard to confirm the handover."],
                  ['Funds Released', 'Escrow instantly releases the payment. The platform takes a small flat fee, and the rest goes to you.'],
                  ['Direct Deposit', 'Funds arrive in your linked bank account automatically, typically within 1-2 business days depending on your bank.'],
                ].map(([title, desc]) => (
                  <div key={title} className="flex gap-4">
                    <div className="mt-1 bg-white/10 p-2 rounded-full flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg uppercase mb-1">{title}</h4>
                      <p className="text-secondary-foreground/80 text-sm">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Success Stories ── */}
    <section className="py-24 bg-muted/30 border-t border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 uppercase">Success Stories</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            See how real people and businesses are turning excess inventory into profit.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              initials: 'MS', bg: 'bg-muted text-primary',
              name: 'Mike Sanchez', role: 'Independent Contractor',
              quote: '"I usually rent a dumpster for leftover siding and drywall. I put it on here instead and sold it to a DIYer the next day. No dumpster fee, plus I made money."',
              earned: '$4,250', period: 'this year',
              cardCls: 'bg-card border border-border',
            },
            {
              initials: 'BT', bg: 'bg-primary text-primary-foreground',
              name: 'BuildTech Solutions', role: 'Commercial Developer',
              quote: '"We over-ordered steel framing for a mid-rise project. Returning it meant a 25% restocking fee plus freight. We listed it locally here and sold the lot to another firm at a tiny discount."',
              earned: '$28,400', period: 'this year',
              cardCls: 'bg-primary/5 border border-primary/20 transform md:-translate-y-4',
            },
            {
              initials: 'SJ', bg: 'bg-muted text-secondary',
              name: 'Sarah Jenkins', role: 'Homeowner / DIY',
              quote: '"After our kitchen reno, we had 4 boxes of expensive subway tile left over. I listed it on Saturday morning, and someone picked it up for a bathroom flip by noon."',
              earned: '$320', period: 'one-time sale',
              cardCls: 'bg-card border border-border',
            },
          ].map(({ initials, bg, name, role, quote, earned, period, cardCls }) => (
            <div key={name} className={`p-8 rounded-xl shadow-sm relative ${cardCls}`}>
              <Quote className="absolute top-6 right-6 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-4 mb-6">
                <div className={`h-12 w-12 rounded-full ${bg} flex items-center justify-center font-bold text-lg`}>{initials}</div>
                <div>
                  <h4 className="font-bold">{name}</h4>
                  <p className="text-xs text-muted-foreground uppercase">{role}</p>
                </div>
              </div>
              <p className="italic text-foreground/80 mb-6">{quote}</p>
              <div className="border-t border-border/50 pt-4">
                <div className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Earned on platform</div>
                <div className="text-2xl font-bold text-green-600">
                  {earned} <span className="text-sm font-normal text-muted-foreground">{period}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-16">
          <NamedLink
            name="NewListingPage"
            className="inline-flex items-center justify-center h-14 px-8 rounded-md bg-primary text-primary-foreground text-lg font-semibold uppercase hover:bg-primary/90"
          >
            Start Selling Today
          </NamedLink>
        </div>
      </div>
    </section>
  </div>
);

export default HowItWorksSection;
